import {
  ArrayOr,
  Assets,
  AssetsBundle,
  AssetsManifest,
  UnresolvedAsset,
} from "pixi.js";
import { DEBUG } from "../AppConfig";

export type LoadingProgress = {
  sectionsToLoad: number;
  sectionsLoaded: number;
  currentSection: string;
  sectionProgress: number;
};

const loadScreenBundleName: string = "load-screen";
let loadScreenBundleLoaded: boolean = false;

const loadAssets = async ({
  onLoadingAssetsLoaded,
  onProgress,
}: {
  onLoadingAssetsLoaded?: () => void;
  onProgress?: (progress: LoadingProgress) => void;
}) => {
  let progress: LoadingProgress = {
    currentSection: "",
    sectionProgress: 0,
    sectionsLoaded: 0,
    sectionsToLoad: 0,
  };

  // Load game manifest
  await loadFromManifest({
    onLoadingAssetsLoaded: onLoadingAssetsLoaded,
    onProgress: (bundleProgress) => {
      progress.currentSection = bundleProgress.currentSection;
      progress.sectionProgress = bundleProgress.sectionProgress;
      progress.sectionsLoaded = bundleProgress.sectionsLoaded;
      onProgress?.({ ...progress });
    },
  });

  // TODO: Load theme manifests
};

const loadFromManifest = async ({
  themeName,
  onLoadingAssetsLoaded,
  onProgress,
}: {
  themeName?: string;
  onLoadingAssetsLoaded?: () => void;
  onProgress?: (progress: LoadingProgress) => void;
}) => {
  const baseUrl = "/assets";

  // 1. Load main manifest (unchanged - your current code)
  const mainManifestRes = await fetch(`${baseUrl}/manifest.json`);
  const mainManifest = (await mainManifestRes.json()) as AssetsManifest;

  if (!mainManifest.bundles) {
    throw new Error("[Assets] Invalid assets manifest");
  }

  // 2. Initialize with main assets (unchanged)
  await Assets.init({
    basePath: baseUrl,
    manifest: mainManifest,
    texturePreference: {
      resolution: 1,
      format: ["png"],
    },
  });

  if (DEBUG) {
    Assets.setPreferences({
      preferWorkers: false,
    });
  }

  // 3. Load main bundles (your existing code)
  const filteredBundles = mainManifest.bundles.filter((bundle) => {
    return ["default", "classic", loadScreenBundleName, themeName].includes(
      bundle.name,
    );
  });

  let loadingProgress: LoadingProgress = {
    currentSection: "",
    sectionProgress: 0,
    sectionsLoaded: -1,
    sectionsToLoad: filteredBundles.length + (themeName ? 1 : 0),
  };

  // Load main bundles...
  let loadBundle;
  if (
    (loadBundle = mainManifest.bundles.find(
      (b) => b.name === loadScreenBundleName,
    ))
  ) {
    console.log(`[Assets] Loading '${loadBundle.name}' bundle:`, loadBundle);
    await Assets.loadBundle(loadBundle.name);

    if (onLoadingAssetsLoaded !== undefined) {
      onLoadingAssetsLoaded();
    }
    loadingProgress.sectionsToLoad = Math.max(
      0,
      loadingProgress.sectionsToLoad - 1,
    );
    loadScreenBundleLoaded = true;
  } else {
    onLoadingAssetsLoaded?.();
  }

  // ... load other main bundles ...
  await Assets.loadBundle("default", (progress) => {
    loadingProgress.currentSection = `Common assets`;
    loadingProgress.sectionProgress = progress;
    if (onProgress) {
      onProgress(loadingProgress);
    }
  });

  // TODO: Do not hardcode this
  await Assets.loadBundle("classic", (progress) => {
    loadingProgress.currentSection = `Classic theme`;
    loadingProgress.sectionProgress = progress;
    if (onProgress) {
      onProgress(loadingProgress);
    }
  });
  // await loadThemeAssets("common", (progess) => {
  // });

  // 4. Load theme assets SEPARATELY (no second Assets.init!)
  if (themeName) {
    await loadThemeAssets(themeName, (progress) => {
      loadingProgress.currentSection = `Theme: ${themeName}`;
      loadingProgress.sectionProgress = progress;
      if (onProgress) {
        onProgress(loadingProgress);
      }
    });
  }
};

// Separate function to load theme assets manually
const loadThemeAssets = async (
  themeName: string,
  onProgress?: (progress: number) => void,
): Promise<void> => {
  const themeBaseUrl = `/themes/${themeName}`;

  try {
    const themeManifestRes = await fetch(`${themeBaseUrl}/manifest.json`);
    const themeManifest = (await themeManifestRes.json()) as AssetsManifest;

    // Manually add theme bundles to the resolver
    for (const bundle of themeManifest.bundles) {
      const bundleAssets = convertAssetsToRecord(bundle, themeBaseUrl);
      console.log(`[Assets] Loading '${bundle.name}' bundle:`, bundleAssets);

      // Add bundle to resolver (doesn't require Assets.init)
      Assets.resolver.addBundle(bundle.name, bundleAssets);

      // Load the bundle
      await Assets.loadBundle(bundle.name, onProgress);
    }
  } catch (error) {
    console.warn(`Failed to load theme '${themeName}':`, error);
  }
};

function isAbsoluteUrl(s: string): boolean {
  return (
    s.startsWith("/") || s.startsWith("http://") || s.startsWith("https://")
  );
}

function normalizeSrcEntry(
  src: ArrayOr<string>,
  basePath: string,
): ArrayOr<string> {
  if (Array.isArray(src)) {
    return src.map((s) =>
      typeof s === "string" && !isAbsoluteUrl(s) ? `${basePath}/${s}` : s,
    );
  }

  return typeof src === "string" && !isAbsoluteUrl(src)
    ? `${basePath}/${src}`
    : src;
}

function convertAssetsToRecord(
  bundle: AssetsBundle,
  basePath: string,
): Record<string, UnresolvedAsset<any>> {
  const assets = bundle.assets;
  const result: Record<string, UnresolvedAsset<any>> = {};

  // Case A: assets is an array of UnresolvedAsset-like entries
  if (Array.isArray(assets)) {
    assets.forEach((asset, idx) => {
      // Asset may be a string or object. Prefer asset.name as key, otherwise use bundleName_idx.
      const key =
        (asset &&
          typeof (asset as any).name === "string" &&
          (asset as any).name) ||
        `${bundle.name}_${idx}`;

      // Normalize object to UnresolvedAsset shape
      if (typeof asset === "string") {
        result[key] = {
          src: normalizeSrcEntry(asset, basePath),
        } as UnresolvedAsset<any>;
      } else {
        // asset is object-like; ensure src is normalized
        const a = { ...(asset as any) } as UnresolvedAsset<any>;
        if ("src" in a) {
          a.src = normalizeSrcEntry(a.src as any, basePath);
        }
        result[key] = a;
      }
    });

    return result;
  }

  // Case B: assets is a single UnresolvedAsset (object with 'src') -> make one entry
  if (
    typeof assets === "object" &&
    assets !== null &&
    "src" in assets &&
    !Array.isArray(assets)
  ) {
    const key = `${bundle.name}_0`;
    const a = { ...(assets as any) } as UnresolvedAsset<any>;
    a.src = normalizeSrcEntry(a.src as any, basePath);
    result[key] = a;
    return result;
  }

  // Case C: assets is a record/object mapping keys to src or UnresolvedAsset
  if (typeof assets === "object" && assets !== null) {
    for (const [key, value] of Object.entries(assets)) {
      if (typeof value === "string") {
        result[key] = {
          src: normalizeSrcEntry(value, basePath),
        } as UnresolvedAsset<any>;
      } else if (Array.isArray(value)) {
        result[key] = {
          src: normalizeSrcEntry(value as any, basePath),
        } as UnresolvedAsset<any>;
      } else if (typeof value === "object" && value !== null) {
        const a = { ...(value as any) } as UnresolvedAsset<any>;
        if ("src" in a) {
          a.src = normalizeSrcEntry(a.src as any, basePath);
        }
        result[key] = a;
      }
    }
    return result;
  }

  // Fallback: return empty record
  return result;
}

export { loadAssets };
